import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Paper } from '@material-ui/core';
import { useMutation, useQuery } from '@apollo/react-hooks';
import React from 'react';
import DetailedTable from '../../components/DetailedTable';
import { Loading } from '../../Loading';
import { TRANSFORM_SPEC_HISTORY, UPDATE_TRANSFORM_SPEC } from '../graphQL/queries';
import Undo from '@jda/lui-common-icon-library/ui/Undo';
import View from '@jda/lui-common-icon-library/ui/ViewDetails';
import ViewSpecDialog from './ViewSpecDialog';
import { useSnackbar } from 'notistack';
import { NetworkStatus } from '@apollo/client';
import moment from 'moment';
import _ from 'lodash';
import { PriorityBar, PriorityType } from '@jda/lui-common-component-library';
import { makeStyles } from '@material-ui/core/styles';
import { formatString } from '../../utils/helpers';

const useStyles = makeStyles(theme => ({
  priorityBarSettings: {
    position: 'relative',
    height: theme.spacing(4),
    width: theme.spacing(0.25)
  }
}));

const HistoryDetailView = (props: any) => {
  const [updateTransformSpec] = useMutation(UPDATE_TRANSFORM_SPEC);
  const { enqueueSnackbar } = useSnackbar();

  const [specData, setSpecData] = React.useState<any>({});
  const [viewSpec, isViewSpec] = React.useState<any>(false);
  const [revertConfirmDialog, isRevertConfirmDialog] = React.useState<any>(false);
  const [historyRowObj, setHistoryRowObj] = React.useState<any>({});

  const classes = useStyles();

  const { loading, data, networkStatus } = useQuery<any, {}>(TRANSFORM_SPEC_HISTORY, {
    variables: {
      participantCode: props.participantCode,
      workflowCode: props.workflowCode,
      operationCode: props.operationCode,
      type: props.type,
      httpStatusCode: props.httpStatus
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true
  });

  const handleRevertConfirmation = (rowData: any) => {
    isRevertConfirmDialog(true);
    setHistoryRowObj(rowData);
  };

  const handleSpecClick = (specData: any) => {
    const specJSON = formatString(specData);
    isViewSpec(true);
    setSpecData(specJSON);
  };

  const handleSpecDialogClose = () => {
    isViewSpec(false);
  };

  const handleRevertDialogClose = () => {
    isRevertConfirmDialog(false);
  };

  const handleRevertDialogSubmit = () => {
    isRevertConfirmDialog(false);
    updateTransformSpec({
      variables: {
        workflowCode: props.workflowCode,
        participantCode: props.participantCode,
        operationCode: historyRowObj.entity.operationCode,
        type: historyRowObj.entity.type,
        httpStatus: historyRowObj.entity.httpStatus,
        spec: historyRowObj.entity.spec,
        draftSpec: historyRowObj.entity.draftSpec,
        comments: `revert from version ${historyRowObj.metadata.delegate.id}`,
        method: 'PUT'
      }
    })
      .then(response => {
        console.log(response);
        enqueueSnackbar('Spec reverted successfully !!', { variant: 'success', preventDuplicate: true });
        //refetch();
        props.historyRefetch();
      })
      .catch(err => {
        enqueueSnackbar('Unable to revert the spec', { variant: 'error', preventDuplicate: true });
        console.log(err);
      });
  };

  const subtableActions: any = [
    props.supportUser !== 'APP_SUPPORT' && {
      icon: () => <Undo data-id="view-history" />,
      onClick: (event: any, rowData: any) => handleRevertConfirmation(rowData),
      tooltip: 'Revert'
    }
  ];
  const linkStyle = { cursor: 'pointer', textDecoration: 'none' };

  const getSpecStatus: any = (draftSpec: any) => {
    if (_.isEmpty(draftSpec)) {
      return PriorityType.LOW;
    } else return PriorityType.MEDIUM;
  };

  const subcolumns = [
    {
      title: '',
      field: 'priority',
      // Type checking is not handled in material-table
      // This fix will be available soon. It will be removed once fix is available.
      width: 5,
      sorting: false,
      cellStyle: { padding: 8, zIndex: 1 },
      headerStyle: {
        padding: 0
      },
      render: (rowData: any) => {
        return (
          <PriorityBar priority={getSpecStatus(rowData.entity.draftSpec)} className={classes.priorityBarSettings} />
        );
      }
    },
    {
      title: 'Version Number',
      field: 'metadata.delegate.id'
    },
    {
      title: 'Updated On',
      field: 'entity.lastUpdated',
      render: (rowData: any) => moment(rowData.entity.lastUpdated).format('MM/DD/YYYY hh:mm:ss A')
    },
    {
      title: 'Updated By',
      field: 'entity.lastUpdatedBy'
    },
    {
      title: 'Comments',
      field: 'entity.comments'
    },
    {
      title: 'Spec',
      field: 'entity.spec',
      render: (rowData: any) => {
        if (rowData.entity.spec) {
          return (
            <View
              data-id="spec-view"
              onClick={() =>
                handleSpecClick(
                  rowData.entity?.draftSpec && !_.isEmpty(rowData.entity.draftSpec)
                    ? rowData.entity.draftSpec
                    : rowData.entity.spec
                )
              }
              style={linkStyle}
            />
          );
        } else {
          return rowData.entity.spec;
        }
      }
    }
  ];

  return (
    <Paper variant="outlined">
      <ViewSpecDialog
        data-id="view-spec-dialog"
        dialogOpen={viewSpec}
        specData={specData}
        handleDialogClose={handleSpecDialogClose}
      />
      <Dialog
        data-id="Revert-spec-dialog"
        open={revertConfirmDialog}
        onClose={handleRevertDialogClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Revert Spec</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">Do you want to revert the spec?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRevertDialogClose} color="primary" variant="outlined">
            No
          </Button>
          <Button
            data-id="revert-dialog-submit"
            onClick={handleRevertDialogSubmit}
            color="primary"
            variant="contained"
            autoFocus
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      {loading || networkStatus === NetworkStatus.refetch ? (
        <Loading />
      ) : (
        <DetailedTable
          id={`TransformSpec-${props.participantCode}-${props.operationCode}-${props.type}`}
          title="View History"
          data-id="ViewHistory"
          columns={subcolumns}
          data={data?.TransformSpecHistory?.content || []}
          showSelection={false}
          showTitle={false}
          showToolbar={false}
          tableActions={subtableActions}
          showPagination
        />
      )}
    </Paper>
  );
};
export default HistoryDetailView;
